import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { BUILDING_GROUPS, BUILDING_MAP } from '../../constants/buildings';
import { formatNumber } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import { deriveReturnTimeFraction } from '../../utils/tickCountdown';
import { GlanceCard } from '../shell/GlanceCard';
import { PercentRow } from '../shell/PercentRow';
import { StrategyGuidanceBanner } from '../strategy/StrategyGuidanceBanner';
import { Toggle } from '../ui/Toggle';
import { MobileRazePanel } from './MobileRazePanel';
import { MobileBuildingOverviewPanel } from './MobileBuildingOverviewPanel';
import { pctForAcres, acresForPct, stepAddByPct } from './buildingsPercent';
import { BUILDINGS_TABS } from '../../constants/buildingsTabs';
import { aggregateQueueBatches } from '../../utils/queueAggregation';
import { QueueSummaryRow } from '../shared/QueueSummaryRow';
import type { BuildingsShellProps } from '../../pages/Buildings';

/** Stable ordinal for sorting aggregated construction groups — flattened `BUILDING_GROUPS` order,
 *  not raw iteration order (which differs between an in-memory representation and a post-DB-load
 *  one, `TrainingQueueAggregator` precedent, SF-170). */
const BUILDING_ORDINAL = BUILDING_GROUPS.flatMap((g) => g.buildings.map((b) => b.id));

/** Matches Layout.tsx/AppShellLayout.tsx's existing reserve for MobileTabBar's footprint (SF-132
 *  Decision 4) — no shared named constant exists yet for this literal (Deferred Item 6). */
const TAB_BAR_OFFSET = '96px';

export function MobileBuildingsShell({
  province,
  activeStrategy,
  strategyLoading,
  activeTab,
  setActiveTab,
  buildQty,
  isAccelerated,
  setIsAccelerated,
  isSubmitting,
  handleSetTargetAcres,
  fillStrategyBlockedReason,
  handleOptimizeAll,
  handleClearAll,
  handleSubmitClick,
  bulkMutationVersion,
  totalLand,
  barren,
  buildings,
  construction,
  inProgress,
  totalBuildings,
  totalBuildQty,
  constructionTime,
  totalCost,
  worldId,
  provinceId,
  refresh,
}: BuildingsShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  // SF-190 Component 2: `addAcres` is the Add DELTA itself, not a resulting total (was
  // `stepper.acres = owned + inProgress + draft`) — the fix for Bug 1 (the stepper's old floor of
  // 0 let it claim "building to 0% of land" even though more was already owned; Build cannot
  // un-build). Representing state as the delta makes that invalid state unrepresentable by
  // construction rather than needing a special-cased clamp.
  const [stepper, setStepper] = useState<{ id: string; addAcres: number } | null>(null);
  const [expandedConstructionBuilding, setExpandedConstructionBuilding] = useState<string | null>(null);
  // Post-SF-190 follow-up: default ON — with every building in the catalog rendered, the strategy
  // signal (what's owned/in-progress/drafted/targeted) gets lost in a long list of untouched rows.
  const [showRelevantOnly, setShowRelevantOnly] = useState(true);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  // SF-143 Critical Issue #3: an external bulk mutation (Clear/Fill Strategy/submit-success) can
  // leave the expanded row's stepper pointing at a now-stale value — collapse rather than let the
  // next tap silently rewrite/resurrect data changed by another route.
  useEffect(() => {
    setStepper(null);
  }, [bulkMutationVersion]);

  function handleToggleRow(buildingId: string) {
    if (stepper?.id === buildingId) {
      setStepper(null);
      return;
    }
    setStepper({ id: buildingId, addAcres: buildQty[buildingId] ?? 0 });
  }

  // SF-190 Component 2: every mutation reconstructs the absolute total (owned + inProgress +
  // addAcres) before calling handleSetTargetAcres — its signature is unchanged (SF-143's
  // acres-first write path), addAcres itself never crosses that boundary.
  function adjustStepper(deltaPct: number) {
    if (!stepper) return;
    const owned = buildings[stepper.id] ?? 0;
    const inProg = inProgress[stepper.id] ?? 0;
    const qty = buildQty[stepper.id] ?? 0;
    const otherTotal = totalBuildQty - qty;
    const rowRemainingBarren = Math.max(0, barren - otherTotal);
    const next = stepAddByPct({ totalLand, currentAddAcres: stepper.addAcres, deltaPct, maxAddAcres: rowRemainingBarren });
    setStepper({ id: stepper.id, addAcres: next });
    handleSetTargetAcres(stepper.id, owned + inProg + next);
  }

  function setStepperAddAcresAbsolute(buildingId: string, addAcres: number) {
    const owned = buildings[buildingId] ?? 0;
    const inProg = inProgress[buildingId] ?? 0;
    setStepper({ id: buildingId, addAcres });
    handleSetTargetAcres(buildingId, owned + inProg + addAcres);
  }

  const bottomBarVisible = activeTab === 'build' && (totalBuildQty > 0 || stepper !== null);
  // SF-190 Component 5: relocating Accelerated into the Construct bar grows its rendered height —
  // bumped from the pre-Accelerated 110px reserve, following the SF-182 stacked-clearance
  // precedent (measure the real addition rather than guessing a round number). Flagged for
  // on-device re-verification, same as SF-182's own bar-height bumps.
  const bottomBarPaddingBottom = bottomBarVisible ? '138px' : undefined;

  // Land headline card values
  const builtPct = pctForAcres(totalLand, totalBuildings);
  const queuedAcres = province.territory.totals.construction;
  const queuedPct = pctForAcres(totalLand, queuedAcres);
  const barrenPct = pctForAcres(totalLand, barren);

  // Under Construction card: one shared current-tick-elapsed fraction for every timer (Components
  // §1 point 5) — no per-entity field needed.
  const returnTimeFraction = deriveReturnTimeFraction(province.sync);

  // SF-179 Component 5: collapse the raw per-order "Under Construction" list to one aggregated row
  // per building. max(ticksRemaining) here is "days until fully complete" for a different reason
  // than Training's — Construction is per-order, not per-tick, so the SF-140/SF-170 "min is
  // degenerate" reasoning doesn't transfer; max is simply the more useful single number.
  const aggregatedConstruction = aggregateQueueBatches(construction, {
    groupKey: (c) => c.building,
    ticksRemaining: (c) => c.ticksRemaining,
    amount: (c) => c.acres,
    compare: (a, b) => BUILDING_ORDINAL.indexOf(a) - BUILDING_ORDINAL.indexOf(b),
  });

  const buildPctRounded = Math.round(pctForAcres(totalLand, totalBuildQty));

  // SF-190 Component 8: gated on an active strategy with at least one positive building target —
  // a bare "100% Unallocated" with no strategy to speak of isn't itself informative, and Component
  // 7's fallback banner already covers the no-strategy case on this page.
  const strategyBuildingTargets = activeStrategy?.settings?.buildings;
  const hasStrategyTargets = strategyBuildingTargets != null
    && Object.values(strategyBuildingTargets).some((v) => (v ?? 0) > 0);
  const unallocatedPct = hasStrategyTargets
    ? Math.max(0, 100 - Object.values(strategyBuildingTargets!).reduce((sum, v) => sum + (v || 0), 0))
    : 0;
  const unallocatedAcres = acresForPct(totalLand, unallocatedPct);

  return (
    <div className="space-y-3" style={bottomBarPaddingBottom ? { paddingBottom: bottomBarPaddingBottom } : undefined}>
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Buildings
      </h1>

      <StrategyGuidanceBanner activeStrategy={activeStrategy} isLoading={strategyLoading} page="growth" compact showFallback />

      <div className="flex border-b" style={{ borderColor: COLORS.border }} role="tablist">
        {BUILDINGS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              id={`buildings-tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`buildings-panel-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
              style={{
                minHeight: 44,
                borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'build' && (
        <div
          role="tabpanel"
          id="buildings-panel-build"
          aria-labelledby="buildings-tab-build"
          className="flex flex-col gap-3"
        >
          {/* Land headline card */}
          <GlanceCard title="Land">
            <div className="flex items-baseline gap-2.5">
              <span className="font-mono text-3xl font-bold" style={{ color: COLORS.text.primary }}>
                {Math.round(builtPct)}%
              </span>
              <span className="text-xs" style={{ color: COLORS.text.tertiary }}>built</span>
              <span className="ml-auto text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>
                {formatNumber(totalBuildings)} / {formatNumber(totalLand)} ac
                <small className="block text-[10px] font-sans" style={{ color: COLORS.text.tertiary }}>
                  {formatNumber(barren)} acres barren
                </small>
              </span>
            </div>
            <div className="h-2 rounded overflow-hidden flex" style={{ backgroundColor: COLORS.bg }} aria-hidden="true">
              {builtPct > 0 && <div style={{ width: `${builtPct}%`, backgroundColor: COLORS.text.secondary }} />}
              {queuedPct > 0 && <div style={{ width: `${queuedPct}%`, backgroundColor: COLORS.gold, opacity: 0.55 }} />}
              {barrenPct > 0 && <div style={{ width: `${barrenPct}%`, backgroundColor: COLORS.bg, borderRight: `1px solid ${COLORS.border}` }} />}
            </div>
            <div className="flex gap-3.5 text-[10px]" style={{ color: COLORS.text.tertiary }}>
              <span>Built {builtPct.toFixed(1)}%</span>
              <span>Queued {queuedPct.toFixed(1)}%</span>
              <span>Barren {barrenPct.toFixed(1)}%</span>
            </div>
          </GlanceCard>

          {/* Under Construction card */}
          {aggregatedConstruction.length > 0 && (
            <GlanceCard title="Under Construction">
              {aggregatedConstruction.map((group) => {
                const name = BUILDING_MAP[group.groupKey]?.name ?? group.groupKey;
                const deltaPct = Math.round(pctForAcres(totalLand, group.total));
                const time = formatDuration(group.maxTicksRemaining, {
                  returnTimeFraction,
                  tickIntervalMs: province.sync.tickIntervalMs,
                });
                const expanded = expandedConstructionBuilding === group.groupKey;
                const rawBatches = construction.filter((c) => c.building === group.groupKey);

                return (
                  <QueueSummaryRow
                    key={group.groupKey}
                    id={`under-construction-${group.groupKey}`}
                    label={name}
                    delta={`+${deltaPct}%`}
                    timeLabel={time}
                    expanded={expanded}
                    onToggle={() => setExpandedConstructionBuilding(expanded ? null : group.groupKey)}
                  >
                    {rawBatches.map((c, idx) => {
                      const batchDeltaPct = Math.round(pctForAcres(totalLand, c.acres));
                      const batchTime = formatDuration(c.ticksRemaining, {
                        returnTimeFraction,
                        tickIntervalMs: province.sync.tickIntervalMs,
                      });
                      return (
                        <div key={`${c.building}-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                          <span style={{ color: COLORS.text.primary }}>
                            {name} <span style={{ color: COLORS.gold }}>+{batchDeltaPct}%</span>
                          </span>
                          <span className="font-mono text-[11px]" style={{ color: COLORS.text.tertiary }}>{batchTime}</span>
                        </div>
                      );
                    })}
                  </QueueSummaryRow>
                );
              })}
            </GlanceCard>
          )}

          {/* Quick actions row */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (fillStrategyBlockedReason) return;
                handleOptimizeAll();
              }}
              aria-disabled={fillStrategyBlockedReason !== null}
              aria-describedby={fillStrategyBlockedReason !== null ? 'fill-strategy-blocked-reason' : undefined}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 rounded-lg"
              style={{
                minHeight: 44,
                backgroundColor: fillStrategyBlockedReason === null ? 'rgba(212,167,44,0.08)' : 'transparent',
                border: `1px solid ${fillStrategyBlockedReason === null ? 'rgba(212,167,44,0.35)' : COLORS.border}`,
                color: fillStrategyBlockedReason === null ? COLORS.gold : COLORS.text.tertiary,
                cursor: fillStrategyBlockedReason === null ? 'pointer' : 'default',
              }}
            >
              ⚡ Fill Strategy
            </button>
          </div>

          {fillStrategyBlockedReason !== null && (
            <span id="fill-strategy-blocked-reason" className="text-xs" style={{ color: COLORS.text.tertiary }}>
              {fillStrategyBlockedReason}
            </span>
          )}

          <div className="text-[10px]" style={{ color: COLORS.text.tertiary }}>
            % of your total land ({formatNumber(totalLand)} acres)
          </div>

          <div className="flex items-center gap-2 py-1">
            <Toggle enabled={showRelevantOnly} onChange={() => setShowRelevantOnly((v) => !v)} />
            <span className="text-xs" style={{ color: COLORS.text.secondary }}>
              Show only relevant buildings
            </span>
          </div>

          {/* Grouped building list — post-SF-190 follow-up: a building is "relevant" when it's
              owned, under construction, drafted this session, or has a strategy target. Filtering
              defaults on since the full catalog buries the current strategy in untouched rows;
              a group with nothing left visible is skipped entirely, not just its rows. */}
          {BUILDING_GROUPS.map((group) => {
            const groupBuildings = showRelevantOnly
              ? group.buildings.filter((b) => {
                  const owned = buildings[b.id] ?? 0;
                  const inProg = inProgress[b.id] ?? 0;
                  const qty = buildQty[b.id] ?? 0;
                  const targetPct = activeStrategy?.settings?.buildings[b.id] ?? 0;
                  return owned > 0 || inProg > 0 || qty > 0 || targetPct > 0;
                })
              : group.buildings;
            if (groupBuildings.length === 0) return null;

            return (
            <div key={group.key} className="flex flex-col gap-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wider px-0.5" style={{ color: COLORS.text.tertiary }}>
                {group.label}
              </div>
              <div className="rounded-lg overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
                {groupBuildings.map((b) => {
                  const owned = buildings[b.id] ?? 0;
                  const inProg = inProgress[b.id] ?? 0;
                  const pct = pctForAcres(totalLand, owned);
                  const inProgPct = inProg > 0 ? Math.round(pctForAcres(totalLand, inProg)) : null;
                  const targetPct = activeStrategy?.settings?.buildings[b.id] ?? null;
                  const expanded = stepper?.id === b.id;

                  const qty = buildQty[b.id] ?? 0;
                  const draftPct = qty > 0 ? Math.round(pctForAcres(totalLand, qty)) : null;

                  return (
                    <PercentRow
                      key={b.id}
                      label={b.name}
                      pct={Math.round(pct)}
                      absolute={owned}
                      targetPct={targetPct !== undefined ? targetPct : null}
                      inProgressPct={inProgPct}
                      draftPct={draftPct}
                      expanded={expanded}
                      disabled={strategyLoading}
                      onToggle={() => handleToggleRow(b.id)}
                    >
                      {expanded && stepper && (() => {
                        const otherTotal = totalBuildQty - qty;
                        const rowRemainingBarren = Math.max(0, barren - otherTotal);
                        const atCeiling = stepper.addAcres >= rowRemainingBarren;
                        const atFloor = stepper.addAcres <= 0;
                        const queued = stepper.addAcres > 0;

                        const nowPct = Math.round(pctForAcres(totalLand, owned + inProg));
                        const addPct = Math.round(pctForAcres(totalLand, stepper.addAcres));

                        // SF-190 Component 2: strategyAddAcres (unclamped) exists only to detect
                        // and report the capped case; appliedAddAcres (clamped) is what's actually
                        // written — addAcres is ALWAYS clamped to [0, rowRemainingBarren] at every
                        // write site, including this one.
                        const strategyAcres = targetPct !== null && targetPct > 0 ? acresForPct(totalLand, targetPct) : null;
                        const strategyAddAcres = strategyAcres !== null ? Math.max(0, strategyAcres - (owned + inProg)) : null;
                        const appliedAddAcres = strategyAddAcres !== null ? Math.min(rowRemainingBarren, strategyAddAcres) : null;
                        const isCapped = strategyAddAcres !== null && strategyAddAcres > rowRemainingBarren;

                        return (
                          <>
                            <div className="flex items-stretch justify-between gap-2 text-center">
                              <div className="flex-1">
                                <span className="block text-[10px]" style={{ color: COLORS.text.tertiary }}>NOW</span>
                                <span className="block font-mono text-lg font-bold" style={{ color: COLORS.text.primary }}>
                                  {nowPct}%
                                </span>
                              </div>
                              <div className="flex-1">
                                <span className="block text-[10px]" style={{ color: COLORS.text.tertiary }}>ADD</span>
                                <span
                                  className="block font-mono text-lg font-bold"
                                  style={{ color: queued ? COLORS.link : COLORS.text.primary }}
                                >
                                  +{addPct}%
                                </span>
                              </div>
                              <div className="flex-1">
                                <span className="block text-[10px]" style={{ color: COLORS.text.tertiary }}>TARGET</span>
                                <span className="block font-mono text-lg font-bold" style={{ color: COLORS.text.primary }}>
                                  {targetPct !== null ? `${targetPct}%` : '—'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                aria-label="Decrease target percentage"
                                onClick={() => adjustStepper(-1)}
                                disabled={atFloor}
                                className="rounded-lg font-mono text-sm"
                                style={{ width: 44, height: 44, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary, opacity: atFloor ? 0.5 : 1 }}
                              >
                                −
                              </button>
                              <span className="flex-1 text-center font-mono text-sm" style={{ color: COLORS.text.tertiary }}>
                                ≈ {formatNumber(stepper.addAcres)} acres
                              </span>
                              <button
                                type="button"
                                aria-label="Increase target percentage"
                                onClick={() => adjustStepper(1)}
                                disabled={atCeiling}
                                className="rounded-lg font-mono text-sm"
                                style={{ width: 44, height: 44, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary, opacity: atCeiling ? 0.5 : 1 }}
                              >
                                +
                              </button>
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                              <button
                                type="button"
                                onClick={() => adjustStepper(1)}
                                disabled={atCeiling}
                                className="text-xs font-semibold rounded-full px-2.5"
                                style={{ minHeight: 44, border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, opacity: atCeiling ? 0.5 : 1 }}
                              >
                                +1%
                              </button>
                              <button
                                type="button"
                                onClick={() => adjustStepper(2)}
                                disabled={atCeiling}
                                className="text-xs font-semibold rounded-full px-2.5"
                                style={{ minHeight: 44, border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, opacity: atCeiling ? 0.5 : 1 }}
                              >
                                +2%
                              </button>
                              {stepper.addAcres > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setStepperAddAcresAbsolute(b.id, 0)}
                                  className="text-xs font-semibold rounded-full px-2.5"
                                  style={{ minHeight: 44, border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary }}
                                >
                                  Clear row (0%)
                                </button>
                              )}
                              {appliedAddAcres !== null && stepper.addAcres !== appliedAddAcres && (
                                <button
                                  type="button"
                                  onClick={() => setStepperAddAcresAbsolute(b.id, appliedAddAcres)}
                                  className="text-xs font-semibold rounded-full px-2.5"
                                  style={{
                                    minHeight: 44,
                                    border: '1px solid rgba(212,167,44,0.4)',
                                    backgroundColor: 'rgba(212,167,44,0.08)',
                                    color: COLORS.gold,
                                  }}
                                >
                                  ⚡ Strategy ({targetPct}%)
                                </button>
                              )}
                              {appliedAddAcres !== null && stepper.addAcres === appliedAddAcres && (
                                <span
                                  className="text-xs font-semibold rounded-full px-2.5 flex items-center"
                                  style={{ minHeight: 44, color: COLORS.text.tertiary }}
                                >
                                  ✓ At strategy ({targetPct}%)
                                </span>
                              )}
                            </div>
                            {isCapped && (
                              <div className="text-[11px]" style={{ color: COLORS.warning }}>
                                Strategy wants {targetPct}%, but only {formatNumber(rowRemainingBarren)} acres of barren land are available.
                              </div>
                            )}
                            <div aria-live="polite" className="sr-only">
                              Add {stepper.addAcres} acres, {addPct}% of land
                            </div>
                          </>
                        );
                      })()}
                    </PercentRow>
                  );
                })}
              </div>
            </div>
            );
          })}

          {hasStrategyTargets && (
            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px dashed ${COLORS.border}` }}>
              <div className="grid grid-cols-[1fr_80px_60px] gap-2 items-center px-3 py-2.5">
                <div>
                  <span className="text-sm italic block" style={{ color: COLORS.text.tertiary }}>Unallocated</span>
                  <span className="text-[10px] block" style={{ color: COLORS.text.tertiary }}>
                    Not assigned to any building — deliberate reserve or simply unallocated
                  </span>
                </div>
                <span className="text-right font-mono text-sm" style={{ color: COLORS.text.tertiary }}>{unallocatedPct}%</span>
                <span className="text-right font-mono text-sm" style={{ color: COLORS.text.tertiary }}>{formatNumber(unallocatedAcres)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'raze' && (
        <div role="tabpanel" id="buildings-panel-raze" aria-labelledby="buildings-tab-raze">
          <MobileRazePanel
            province={province}
            activeStrategy={activeStrategy}
            strategyLoading={strategyLoading}
            onRazeSuccess={refresh}
            worldId={worldId ?? ''}
            provinceId={provinceId ?? ''}
          />
        </div>
      )}

      {activeTab === 'overview' && (
        <div role="tabpanel" id="buildings-panel-overview" aria-labelledby="buildings-tab-overview">
          <MobileBuildingOverviewPanel province={province} />
        </div>
      )}

      {bottomBarVisible && (
        <div
          data-testid="build-bottom-bar"
          className="fixed left-0 right-0 flex flex-col gap-2"
          style={{
            bottom: TAB_BAR_OFFSET,
            backgroundColor: '#0b0e19',
            borderTop: `1px solid ${COLORS.border}`,
            padding: '10px 14px',
            paddingBottom: `calc(10px + env(safe-area-inset-bottom))`,
            zIndex: 30,
          }}
        >
          {/* SF-190 Component 5: relocated from the quick-actions row — cause (the checkbox) and
              effect (the cost/time numbers just below) now render together. */}
          <label
            className="flex items-center gap-1.5 text-xs"
            style={{ color: COLORS.text.tertiary }}
          >
            <input
              type="checkbox"
              checked={isAccelerated}
              onChange={(e) => setIsAccelerated(e.target.checked)}
              className="w-4 h-4 rounded"
              style={{ accentColor: COLORS.gold }}
            />
            Accelerated (2× cost, ½ time)
          </label>
          {totalBuildQty > 0 ? (
            <div className="flex items-baseline justify-between text-[11px]" style={{ color: COLORS.text.tertiary }}>
              <span>
                Building{' '}
                <b className="font-mono" style={{ color: COLORS.text.primary }}>
                  +{buildPctRounded}% of land
                </b>{' '}
                ({formatNumber(totalBuildQty)} acres)
              </span>
              <span className="font-mono font-bold" style={{ color: COLORS.text.primary }}>
                {formatNumber(totalCost)} gc · {constructionTime} days
              </span>
            </div>
          ) : (
            <div className="text-[11px]" style={{ color: COLORS.text.tertiary }}>
              No buildings queued yet — adjust a row above to add one.
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (totalBuildQty === 0) return;
                handleClearAll();
              }}
              aria-disabled={totalBuildQty === 0}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold"
              style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, minHeight: 44, opacity: totalBuildQty === 0 ? 0.5 : 1 }}
            >
              Clear
            </button>
            <button
              onClick={() => {
                if (isSubmitting || totalBuildQty === 0) return;
                handleSubmitClick();
              }}
              aria-disabled={isSubmitting || totalBuildQty === 0}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold"
              style={{
                backgroundColor: COLORS.btnPrimary,
                border: `1px solid ${COLORS.btnPrimaryHover}`,
                color: COLORS.text.primary,
                minHeight: 44,
                opacity: isSubmitting || totalBuildQty === 0 ? 0.5 : 1,
              }}
            >
              Construct
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
